import { isEmpty, isNil, omitBy } from 'lodash';
import { ChannelGroupBrand, NameI18n } from './ChannelGroup';

export const BRAND_NAME_LANGUAGES: (keyof NameI18n)[] = ['ja', 'en', 'zh-TW'];

const trimToEmpty = (value?: string | null): string =>
  typeof value === 'string' ? value.trim() : '';

/**
 * 取當前語系的品牌名稱；沒填就回空字串。
 *
 * 刻意不退到其他語系：日文介面拿中文品牌名去搜 Google Places，命中率反而比不帶品牌更差，
 * 顯示上也會在日文畫面冒出中文。
 */
export function getBrandName(
  brand: ChannelGroupBrand | null | undefined,
  language: string | undefined
): string {
  if (!brand?.nameI18n || !language) {
    return '';
  }
  return trimToEmpty(brand.nameI18n[language as keyof NameI18n]);
}

/** 沒設定圖示時回空字串，地圖那側看到空字串就畫系統預設的 pin。 */
export function getBrandIconUrl(url: string | null | undefined): string {
  return trimToEmpty(url);
}

/**
 * 表單初值。後端對沒填的語系送 null 或整個 key 不存在，輸入框需要的是字串，
 * 所以在這裡補成空字串；圖示 URL 原樣保留，送出時若表單沒有對應的圖片欄位就沿用它。
 */
export function toBrandFormValues(
  brand: ChannelGroupBrand | null | undefined
): ChannelGroupBrand {
  return {
    ...(brand ?? {}),
    nameI18n: {
      ja: trimToEmpty(brand?.nameI18n?.ja),
      en: trimToEmpty(brand?.nameI18n?.en),
      'zh-TW': trimToEmpty(brand?.nameI18n?.['zh-TW'])
    },
    url: trimToEmpty(brand?.url)
  };
}

/**
 * 組出要送給後端的 brand。
 *
 * 文字欄位 trim 後為空就不帶，與後端「trim 後為空存 null」的規則一致，所以重新載入後
 * 表單看到的值和送出前相同。全部欄位都空時整個 brand 不帶，後端會把它存成 null。
 */
export function buildBrandPayload(
  brand: ChannelGroupBrand | null | undefined,
  iconUrls: Pick<ChannelGroupBrand, 'filteredIconUrl' | 'normalIconUrl'>
): ChannelGroupBrand | undefined {
  const nameI18n = BRAND_NAME_LANGUAGES.reduce<Partial<NameI18n>>(
    (result, language) => {
      const name = trimToEmpty(brand?.nameI18n?.[language]);
      return name ? { ...result, [language]: name } : result;
    },
    {}
  );
  const payload: ChannelGroupBrand = omitBy(
    {
      nameI18n: isEmpty(nameI18n) ? undefined : nameI18n,
      url: trimToEmpty(brand?.url) || undefined,
      filteredIconUrl: trimToEmpty(iconUrls.filteredIconUrl) || undefined,
      normalIconUrl: trimToEmpty(iconUrls.normalIconUrl) || undefined
    },
    isNil
  );
  return isEmpty(payload) ? undefined : payload;
}
